<template>
  <button
    :class="[
      'header-menu-button',
      { 'header-menu-button--activated': isMenuOpen },
    ]"
    @click="click"
  >
    <div class="user">
      <UserAvatar :avatar-url="userAvatar" size="small" />
      <div class="user__info">
        <div class="user__info-name">
          {{ userName }}
        </div>
        <ul class="user__info-roles">
          <li v-for="role in userRoles" :key="role">
            {{ role }}
          </li>
        </ul>
      </div>
    </div>
    <ChevronIcon
      class="header-menu-button__chevron-icon"
      :direction="chevronDirection"
    />
  </button>
</template>

<script>
import ChevronIcon from '@/components/icon_components/ChevronIcon.vue'
import UserAvatar from '@/components/user/edit/UserAvatar.vue'
import { useMe } from '@/hooks/swr/useMe'

const MAX_NUMBER_OF_ROLES = 4

export default {
  components: {
    UserAvatar,
    ChevronIcon,
  },
  props: {
    isMenuOpen: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['click'],
  setup() {
    const { data: currentUser } = useMe()
    return { currentUser }
  },
  computed: {
    chevronDirection() {
      return this.isMenuOpen ? 'up' : 'down'
    },
    userAvatar() {
      return this.currentUser?.image
    },
    userRoles() {
      const hasUserRoles = this.currentUser?.roles.length > 0

      if (hasUserRoles) {
        return this.currentUser.roles.filter(
          (item, index) => index < MAX_NUMBER_OF_ROLES
        )
      } else {
        return [this.$t('USER')]
      }
    },
    userName() {
      if (this.currentUser) {
        return `${this.currentUser.first_name} ${this.currentUser.last_name}`
      } else {
        return '-'
      }
    },
  },
  methods: {
    click() {
      this.$emit('click')
    },
  },
}
</script>

<style scoped lang="scss">
.header-menu-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 300px;
  height: 100%;
  border: solid 1px $color-border;
  border-radius: $border-radius-l;
  background: $color-canvas;
  padding: $spacing-4xs $spacing-3xs $spacing-4xs $spacing-4xs;
  cursor: pointer;
  gap: $spacing-xs;
  z-index: $base-header-z-index;

  &:hover,
  &--activated {
    background: $color-paper;
  }

  &--activated {
    border-radius: $border-radius-l $border-radius-l 0 0;
  }

  &__chevron-icon {
    width: $spacing-2xs;
    height: $spacing-2xs;
  }
}

.user {
  display: flex;
  align-items: center;
  gap: $spacing-3xs;
  width: 100%;

  &__info {
    display: flex;
    flex-direction: column;
    text-align: left;
  }
  &__info-name {
    @include subtitle-2;
    font-size: $font-size-m;
    color: $color-text;
  }
  &__info-roles {
    display: flex;
    gap: $spacing-5xs;
    font-size: $font-size-s;
    color: $color-text;
    text-transform: capitalize;
  }
}

@media screen and (max-width: $tablet-width-max) {
  .header-menu-button {
    border: none;
  }

  .header-menu-button:hover,
  .header-menu-button--activated {
    background: unset;
  }
}

@media screen and (max-width: $mobile-width-max) {
  .header-menu-button {
    justify-content: center;
    padding: $spacing-5xs;
    min-width: initial;

    &__chevron {
      display: none;
    }
  }
  .user {
    &__info {
      display: none;
    }
  }
}
</style>
